import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, IconButton, Stack, Typography, ToggleButton, ToggleButtonGroup, CircularProgress, Fade, Paper, useTheme } from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import Webcam from 'react-webcam';
import Cropper from 'react-easy-crop';
import type { Area } from 'react-easy-crop';
import { getCroppedImg } from '../../../utils/getCroppedImg';
import { FilePond, registerPlugin } from 'react-filepond';
import type { FilePondFile } from 'filepond';
import type { FilePondProps } from 'react-filepond';
import 'filepond/dist/filepond.min.css';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';

registerPlugin(FilePondPluginImagePreview);

type ImageInputProps = {
    onChange: (files: File[]) => void;
    initialFiles?: File[];
    server?: FilePondProps['server'];
    label?: string;
    maxFiles?: number;
    variant?: 'default' | 'simple';
};

export const ImageInput: React.FC<ImageInputProps> = ({
    onChange,
    initialFiles = [],
    server,
    label = 'Imagem',
    maxFiles = 1,
    variant = 'default',
}) => {
    const theme = useTheme();
    const [mode, setMode] = useState<'upload' | 'capture'>('capture');
    const [files, setFiles] = useState<any[]>([]);
    const [captureOpen, setCaptureOpen] = useState(false);
    const [cropOpen, setCropOpen] = useState(false);
    const [capturedImg, setCapturedImg] = useState<string | null>(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
    const webcamRef = useRef<Webcam>(null);
    const [webcamLoading, setWebcamLoading] = useState(true);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    
    // Ref para evitar loops
    const lastFilesRef = useRef<File[]>([]);

    useEffect(() => {
        if (initialFiles.length) {
            setFiles(
                initialFiles.map((file) => ({
                    source: file,
                    file,
                    options: { type: 'local' },
                }))
            );
        }
    }, [initialFiles]);

    // Correção principal: useCallback para memoizar onChange e comparação de arquivos
    const handleFilesChange = useCallback((newFiles: File[]) => {
        // Comparar se os arquivos realmente mudaram
        const filesChanged = newFiles.length !== lastFilesRef.current.length ||
            newFiles.some((file, index) => file !== lastFilesRef.current[index]);
        
        if (filesChanged) {
            lastFilesRef.current = newFiles;
            onChange(newFiles);
        }
    }, [onChange]);

    useEffect(() => {
        const fileObjs = files
            .map((f: FilePondFile) => f.file)
            .filter(Boolean) as File[];
        
        handleFilesChange(fileObjs);
    }, [files, handleFilesChange]);

    const handleCapture = useCallback(() => {
        if (!webcamRef.current) return;
        const imageSrc = webcamRef.current.getScreenshot();
        if (!imageSrc) return;
        setCapturedImg(imageSrc);
        setCaptureOpen(false);
        setCropOpen(true);
    }, []);

    const handleCropSave = useCallback(async () => {
        if (!capturedImg || !croppedAreaPixels) return;
        const croppedBlob = await getCroppedImg(capturedImg, croppedAreaPixels);
        const file = new File([croppedBlob], `captured-${Date.now()}.png`, { type: 'image/png' });
        setFiles([
            {
                source: file,
                file,
                options: { type: 'local' },
            },
        ]);
        setCropOpen(false);
        setCapturedImg(null);
    }, [capturedImg, croppedAreaPixels]);

    const handleModeChange = (_: any, value: 'upload' | 'capture') => {
        if (value) setMode(value);
    };

    const handleWebcamUserMedia = () => setWebcamLoading(false);

    // Melhorar o handling de update dos files do FilePond
    const handleUpdateFiles = useCallback((fileItems: any[]) => {
        setFiles(fileItems);
    }, []);

    // Limpar arquivos
    const handleClearFiles = useCallback(() => {
        setFiles([]);
    }, []);

    const previewUrl = files[0]?.file
        ? URL.createObjectURL(files[0].file)
        : files[0]?.source && typeof files[0].source !== 'string'
            ? URL.createObjectURL(files[0].source)
            : undefined;

    if (variant === 'simple') {
        return (
            <Box sx={{ width: '100%' }}>
                {label && (
                    <Typography fontWeight={700} fontSize={15} mb={1}>
                        {label}
                    </Typography>
                )}
                <Box
                    sx={{
                        border: '2px dashed #C7C7C7',
                        borderRadius: 3,
                        p: 0,
                        minHeight: 180,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        background: '#FAFAFA',
                        mb: 2,
                        width: '100%',
                        maxWidth: 500,
                        mx: 'auto',
                    }}
                >
                    {previewUrl ? (
                        <Box sx={{ position: 'relative', width: 180, height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <img
                                src={previewUrl}
                                alt="preview"
                                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 16, boxShadow: '0 2px 8px 0 rgba(16,30,54,0.08)' }}
                            />
                            <IconButton
                                size="small"
                                sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    bgcolor: 'rgba(255,255,255,0.85)',
                                    color: '#333',
                                    zIndex: 3,
                                    '&:hover': { bgcolor: '#fff', color: 'red' },
                                    border: '1px solid #eee',
                                }}
                                onClick={handleClearFiles}
                            >
                                <CloseIcon fontSize="medium" />
                            </IconButton>
                        </Box>
                    ) : (
                        <>
                            <CameraAltIcon sx={{ fontSize: 48, color: '#BDBDBD', mb: 1 }} />
                            <Typography color="#888" fontSize={16} mb={2}>
                                Clique para adicionar foto
                            </Typography>
                            <Button
                                variant="contained"
                                sx={{
                                    background: '#22C55E',
                                    color: '#fff',
                                    fontWeight: 700,
                                    fontSize: 16,
                                    borderRadius: 2,
                                    px: 4,
                                    py: 1.5,
                                    boxShadow: 'none',
                                    '&:hover': { background: '#16A34A' },
                                }}
                                onClick={() => {
                                    if (fileInputRef.current) fileInputRef.current.click();
                                }}
                            >
                                Escolher Foto
                            </Button>
                        </>
                    )}
                    <Box sx={{ width: 0, height: 0, overflow: 'hidden', position: 'absolute' }}>
                        <FilePond
                            files={files}
                            onupdatefiles={handleUpdateFiles}
                            allowMultiple={maxFiles > 1}
                            maxFiles={maxFiles}
                            allowReorder={false}
                            allowImagePreview={false}
                            allowDrop={true}
                            allowReplace={true}
                            allowRemove={true}
                            server={server}
                            name="images"
                            labelIdle=""
                            acceptedFileTypes={['image/jpeg', 'image/png']}
                            stylePanelAspectRatio="1:1"
                            stylePanelLayout={null}
                            credits={false}
                            ref={(pond: any) => {
                                if (pond && pond._pond) {
                                    const input = pond._pond.element.querySelector('input[type="file"]');
                                    if (input) fileInputRef.current = input;
                                }
                            }}
                        />
                    </Box>
                </Box>
            </Box>
        );
    }

    return (
        <Stack spacing={2} sx={{ width: '100%' }}>
            <Typography fontWeight={700} fontSize={15} color={theme.palette.text.primary}>
                {label}
            </Typography>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 4,
                    alignItems: { xs: 'center', sm: 'flex-end' },
                    width: '100%',
                }}
            >
                <Paper 
                    elevation={2} 
                    sx={{ 
                        p: 3, 
                        borderRadius: 3, 
                        minWidth: 260, 
                        bgcolor: theme.palette.mode === 'dark' 
                            ? theme.palette.grey[800] 
                            : theme.palette.grey[50], 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        gap: 2,
                        border: `1px solid ${theme.palette.divider}`
                    }}
                >
                    <ToggleButtonGroup
                        value={mode}
                        exclusive
                        onChange={handleModeChange}
                        sx={{ 
                            mb: 1, 
                            width: '100%',
                            '& .MuiToggleButton-root': {
                                color: theme.palette.text.primary,
                                borderColor: theme.palette.divider,
                                '&.Mui-selected': {
                                    backgroundColor: theme.palette.primary.main,
                                    color: theme.palette.primary.contrastText,
                                    '&:hover': {
                                        backgroundColor: theme.palette.primary.dark,
                                    }
                                }
                            }
                        }}
                    >
                        <ToggleButton value="upload" sx={{ flex: 1, py: 2, fontWeight: 700, fontSize: 16 }}>
                            <CloudUploadIcon sx={{ mr: 1 }} /> Upload
                        </ToggleButton>
                        <ToggleButton value="capture" sx={{ flex: 1, py: 2, fontWeight: 700, fontSize: 16 }}>
                            <CameraAltIcon sx={{ mr: 1 }} /> Capturar
                        </ToggleButton>
                    </ToggleButtonGroup>
                    {mode === 'upload' && (
                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            onClick={() => {
                                if (fileInputRef.current) fileInputRef.current.click();
                            }}
                            sx={{ fontWeight: 700, fontSize: 16, borderRadius: 2, py: 1.5, mt: 1, mb: 1 }}
                        >
                            Selecionar Imagem
                        </Button>
                    )}
                    {mode === 'capture' && (
                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            startIcon={<CameraAltIcon />}
                            onClick={() => {
                                setWebcamLoading(true);
                                setCaptureOpen(true);
                            }}
                            sx={{ fontWeight: 700, fontSize: 16, borderRadius: 2, py: 1.5, mt: 1, mb: 1 }}
                        >
                            Tirar Foto
                        </Button>
                    )}

                    <Box sx={{ width: 0, height: 0, overflow: 'hidden', position: 'absolute' }}>
                        <FilePond
                            files={files}
                            onupdatefiles={handleUpdateFiles}
                            allowMultiple={maxFiles > 1}
                            maxFiles={maxFiles}
                            allowReorder={false}
                            allowImagePreview={false}
                            allowDrop={true}
                            allowReplace={true}
                            allowRemove={true}
                            server={server}
                            name="images"
                            labelIdle=""
                            acceptedFileTypes={['image/jpeg', 'image/png']}
                            stylePanelAspectRatio="1:1"
                            stylePanelLayout={null}
                            credits={false}
                            ref={(pond: any) => {
                                if (pond && pond._pond) {
                                    const input = pond._pond.element.querySelector('input[type="file"]');
                                    if (input) fileInputRef.current = input;
                                }
                            }}
                        />
                    </Box>
                </Paper>

                <Fade in={!!previewUrl}>
                    <Box sx={{
                        minWidth: 180,
                        minHeight: 180,
                        width: { xs: '100%', sm: 180 },
                        height: { xs: 'auto', sm: 180 },
                        maxWidth: 300,
                        aspectRatio: '1',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '10%',
                        border: previewUrl 
                            ? `3px solid ${theme.palette.primary.main}` 
                            : `2px dashed ${theme.palette.divider}`,
                        bgcolor: theme.palette.background.paper,
                        position: 'relative',
                        boxShadow: previewUrl ? 3 : 0,
                        overflow: 'hidden',
                        transition: 'border 0.2s'
                    }}>
                        {previewUrl ? (
                            <>
                                <img
                                    src={previewUrl}
                                    alt="preview"
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        borderRadius: '10%',
                                        background: theme.palette.background.paper,
                                    }}
                                />
                                <IconButton
                                    size="small"
                                    sx={{
                                        position: 'absolute',
                                        top: '15%',
                                        right: '-7%',
                                        transform: 'translate(-50%, -50%)',
                                        bgcolor: theme.palette.mode === 'dark' 
                                            ? 'rgba(255,255,255,0.15)' 
                                            : 'rgba(255,255,255,0.85)',
                                        color: theme.palette.text.primary,
                                        zIndex: 3,
                                        transition: 'background 0.2s, color 0.2s',
                                        '&:hover': {
                                            bgcolor: theme.palette.background.paper,
                                            color: theme.palette.primary.main,
                                            boxShadow: 3,
                                        },
                                        border: `2px solid ${theme.palette.background.paper}`,
                                        backdropFilter: 'blur(2px)',
                                    }}
                                    onClick={handleClearFiles}
                                >
                                    <CloseIcon fontSize="medium" />
                                </IconButton>
                            </>
                        ) : (
                            <Typography color={theme.palette.text.secondary} fontSize={16} align="center">
                                Nenhuma imagem selecionada
                            </Typography>
                        )}
                    </Box>
                </Fade>
            </Box>

            <Dialog 
                open={captureOpen} 
                onClose={() => setCaptureOpen(false)} 
                maxWidth="xs" 
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: theme.palette.background.paper,
                        color: theme.palette.text.primary
                    }
                }}
            >
                <DialogContent sx={{ 
                    p: 0, 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    minHeight: 320, 
                    justifyContent: 'center',
                    backgroundColor: theme.palette.background.paper
                }}>
                    {webcamLoading && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 240 }}>
                            <CircularProgress color="primary" />
                        </Box>
                    )}
                    <Webcam
                        ref={webcamRef}
                        audio={false}
                        screenshotFormat="image/png"
                        videoConstraints={{ facingMode: 'user' }}
                        style={{ 
                            width: '100%', 
                            maxWidth: 320, 
                            borderRadius: 8, 
                            display: webcamLoading ? 'none' : 'block' 
                        }}
                        onUserMedia={handleWebcamUserMedia}
                    />
                </DialogContent>
                <DialogActions sx={{ backgroundColor: theme.palette.background.paper }}>
                    <Button onClick={() => setCaptureOpen(false)} color="inherit">
                        Cancelar
                    </Button>
                    <Button 
                        onClick={handleCapture} 
                        variant="contained" 
                        disabled={webcamLoading}
                        color="primary"
                    >
                        Capturar
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog 
                open={cropOpen} 
                onClose={() => setCropOpen(false)} 
                maxWidth="xs" 
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: theme.palette.background.paper,
                        color: theme.palette.text.primary
                    }
                }}
            >
                <DialogContent sx={{ 
                    p: 0, 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    minHeight: 380,
                    backgroundColor: theme.palette.background.paper
                }}>
                    <Typography sx={{ 
                        fontWeight: 700, 
                        mb: 1, 
                        mt: 2, 
                        color: theme.palette.text.primary 
                    }}>
                        Recortar Imagem
                    </Typography>
                    <Box sx={{ 
                        width: 260, 
                        height: 260, 
                        position: 'relative', 
                        bgcolor: theme.palette.mode === 'dark' 
                            ? theme.palette.grey[900] 
                            : theme.palette.grey[800], 
                        borderRadius: 2, 
                        overflow: 'hidden' 
                    }}>
                        {capturedImg && (
                            <Cropper
                                image={capturedImg}
                                crop={crop}
                                zoom={zoom}
                                aspect={1}
                                cropShape="round"
                                showGrid={false}
                                onCropChange={setCrop}
                                onZoomChange={setZoom}
                                onCropComplete={(_, areaPixels) => setCroppedAreaPixels(areaPixels)}
                            />
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ backgroundColor: theme.palette.background.paper }}>
                    <Button onClick={() => setCropOpen(false)} color="inherit">
                        Cancelar
                    </Button>
                    <Button 
                        onClick={handleCropSave} 
                        variant="contained" 
                        color="primary"
                    >
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};

export default ImageInput;